import {clsx} from 'clsx'

import {Bento, SectionIntro, Testimonial, Text} from '@primer/react-brand'

import {Image} from '../../../../../components/Image/Image'
import Spacer from '../../components/Spacer/Spacer'

import COPY, {bentoIcons} from './Features.data'
import Styles from './Features.module.css'

export interface FeaturesProps {
  title: string
  description: string
  bentos: Array<{
    id: string
    title: string
    description: string
    icon: keyof typeof bentoIcons
    image: {
      urlDesktop: string
      description?: string
    }
  }>
  testimonial: {
    quote: string
    name: string
    position: string
  }
}

const defaultProps: Partial<FeaturesProps> = {}

const Features: React.FC<FeaturesProps> = props => {
  const initializedProps = {...defaultProps, ...props}

  const {title, description, bentos, testimonial} = initializedProps

  return (
    <section id="features" className={Styles.Section}>
      <div className="lp-Container">
        <Spacer size="32px" size768="64px" />

        <SectionIntro align="center" fullWidth>
          <SectionIntro.Heading as="h2" size="3">
            {title}
          </SectionIntro.Heading>

          <SectionIntro.Description className={Styles.SectionIntroDescription}>{description}</SectionIntro.Description>
        </SectionIntro>

        <Spacer size="24px" size768="64px" />

        <Bento>
          {/* Hero bento */}
          <Bento.Item
            columnSpan={12}
            style={{
              backgroundSize: 'cover',
              backgroundImage: `url("${COPY.bentos[0].backgroundImage}")`,
            }}
          >
            <Bento.Visual horizontalAlign="center" fillMedia={false} className="lp-Features-bentoVisual1">
              <Image
                src={bentos[0]?.image.urlDesktop}
                alt={bentos[0]?.image.description || ''}
                width="986"
                height="555"
                loading="lazy"
                className="lp-Features-bentoImg1"
              />
            </Bento.Visual>
          </Bento.Item>

          {/* Other bentos */}
          {bentos.slice(1).map((bento, index) => {
            const Icon = bentoIcons[bento.icon] || null
            return (
              <Bento.Item
                key={bento.id}
                className="lp-Features-bento2"
                columnSpan={{xsmall: 12, medium: 6}}
                style={{
                  backgroundSize: 'cover',
                  backgroundImage: `url(${COPY.bentos[index + 1]?.backgroundImage})`,
                }}
              >
                <Bento.Content
                  leadingVisual={Icon && <Icon size={16} />}
                  className="lp-BentoContent lp-Bento--withBadge"
                >
                  <Bento.Heading as="h3" size="5" className="text-wrap-balance">
                    {bento.title}
                  </Bento.Heading>

                  <Text variant="default" size="300" className={clsx(Styles.BentoDescription, 'mt-3')}>
                    {bento.description}
                  </Text>
                </Bento.Content>

                <Bento.Visual
                  fillMedia={false}
                  padding="normal"
                  horizontalAlign="center"
                  verticalAlign="center"
                  className={Styles.BentoVisual}
                >
                  <Image
                    src={bento.image.urlDesktop}
                    alt={bento.image.description || ''}
                    width="529"
                    height="189"
                    loading="lazy"
                    className="lp-Features-bentoImg lp-borderRadiusNone"
                  />
                </Bento.Visual>
              </Bento.Item>
            )
          })}
        </Bento>

        <Spacer size="20px" size768="32px" />

        <Testimonial
          variant="subtle"
          size="large"
          quoteMarkColor="green"
          className="lp-Features-testimonial px-4 px-md-16"
        >
          <Testimonial.Quote>{testimonial.quote}</Testimonial.Quote>
          <Testimonial.Name position={testimonial.position}>{testimonial.name}</Testimonial.Name>
        </Testimonial>
      </div>
    </section>
  )
}

export default Features
